import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SettingsService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { limitToTwoDecimals } from "../../shared/utils/functions";
import { useTranslation } from "react-i18next";
import { ISettings } from "../../shared/interfaces/settings/ISettings";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const SettingsForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [settingsData, setSettingsData] = useState<ISettings | null>(null);

  const settingsSchema = yup.object().shape({
    materialFactor: yup.number().notRequired(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ISettings>({
    defaultValues: {
      materialFactor: 0,
    },
    resolver: yupResolver(settingsSchema) as unknown as Resolver<ISettings>,
  });

  const { unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = async (data: ISettings) => {
    await SettingsService.update(data)
      .then(() => {
        SuccessToast(t(`settings.successUpdate`));
        // Keeps the unsaved changes guard from firing after a successful save
        reset(data);
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await SettingsService.get();
        setSettingsData(settings);
      } catch (error) {
        ErrorToast(error);
      }
    };

    fetchSettings();
  }, [id]);

  useEffect(() => {
    if (settingsData) {
      reset(settingsData);
    }
  }, [settingsData, reset]);
  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <Form.Label className="required">
                      {t("settings.materialFactor")}
                    </Form.Label>
                    <Controller
                      name="materialFactor"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Form.Control
                            type="number"
                            step="0.01"
                            className={`form-control ${errors.materialFactor
                              ? "is-invalid"
                              : ""
                              }`}
                            placeholder={t("settings.materialFactor")}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const limited = limitToTwoDecimals(e.target.value);
                              field.onChange(
                                limited === "" ? undefined : Number(limited)
                              );
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t("settings.updateSettings")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default SettingsForm;
